import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import debounce from 'lodash/debounce';
import {useCallback, useEffect} from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {requestContext} from '../../../../../../common/requestContext';
import logger from '../../../../../../log/logger';
import {debuggingFeature} from '../../../../../../logging/debuggingFeature.variable';
import {
  TrackEventObject,
  UsageScrollObject,
} from '../../../../../../logging/TrackEventObject.interface';
import {
  lastLoggingPageviewContext,
  trackEvent,
} from '../../../../../../logging/trackEvent';

/**
 * obj 가 다를 때만 이벤트를 전송합니다
 * obj 내부 데이터가 완전히 다른지 확인하기 위해 useDeepCompareEffect 를 사용했습니다
 */
export function TrackEventFetcherHeadless({
  obj,
  resendLogOnVisibilityChange = false,
}: {
  obj: TrackEventObject;
  resendLogOnVisibilityChange: boolean;
}) {
  const {isWebView} = requestContext.requestInfo.display;

  const handleUsageEvent = useCallback(async () => {
    const usageEventObj = createUsageEventObject(
      lastLoggingPageviewContext.obj,
      lastLoggingPageviewContext.loggingTime,
      lastLoggingPageviewContext.scrollObj
    );
    if (usageEventObj) {
      if (debuggingFeature.consoleLog) {
        console.log('trackEventObj %j', usageEventObj);
      }
      await trackEvent(usageEventObj);
    }
  }, []);

  useDeepCompareEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        await trackEvent(obj);
      }
    };

    async function sendTrackEvent() {
      try {
        await handleUsageEvent();
        if (debuggingFeature.consoleLog) {
          console.log('trackEventObj %j', obj);
        }
        await trackEvent(obj);
      } catch (e) {
        logger.err('페이지뷰 로깅 에러', e);
      }
    }
    sendTrackEvent();

    if (obj.actionType === 'Pageview') {
      // 현재의 pageview 정보를 저장해둡니다. ( 다음 Usage 전송을 위해서 )
      lastLoggingPageviewContext.obj = obj;
      lastLoggingPageviewContext.loggingTime = new Date();
    }

    window.addEventListener('pagehide', handleUsageEvent);
    if (isWebView && resendLogOnVisibilityChange) {
      window.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      window.removeEventListener('pagehide', handleUsageEvent);
      if (isWebView && resendLogOnVisibilityChange) {
        window.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [obj, handleUsageEvent]);

  // 현재의 페이지의 scroll 정보를 저장해둡니다. ( 다음 Usage 전송을 위해서 )
  useEffect(() => {
    if (!obj.shouldSendUsageScroll) return;
    const handleScrollUsageHeight = debounce(() => {
      lastLoggingPageviewContext.scrollObj.scrollPercent = String(
        Math.round(
          ((window.scrollY + window.innerHeight) /
            document.documentElement.scrollHeight) *
            100
        )
      );
      lastLoggingPageviewContext.scrollObj.scrollHeight = String(
        document.documentElement.scrollHeight
      );
      lastLoggingPageviewContext.scrollObj.scrollTop = String(window.scrollY);
      lastLoggingPageviewContext.scrollObj.scrollBottom = String(
        window.scrollY + window.innerHeight
      );
      lastLoggingPageviewContext.scrollObj.scrollInnerHeight = String(
        window.innerHeight
      );
    }, 100);

    // scroll이 발생하지 않아도 마운트 되면 한번 값을 넣어줘야 합니다.
    handleScrollUsageHeight();

    window.addEventListener('scroll', handleScrollUsageHeight);
    return () => {
      window.removeEventListener('scroll', handleScrollUsageHeight);
    };
  }, [obj.shouldSendUsageScroll]);

  return null;
}

// 직전 Pageview를 기반으로 Usage Event Object를 생성합니다.
function createUsageEventObject(
  obj?: TrackEventObject,
  loggingTime?: Date,
  scrollObj?: UsageScrollObject
): TrackEventObject | undefined {
  // Usage를 보낼 경우가 아니면 undefined를 반환합니다.
  if (
    !obj ||
    obj.actionType !== 'Pageview' ||
    !obj.shouldSendUsagePage ||
    !loggingTime
  ) {
    return;
  }

  // usage에 scroll 값을 포함하지 않아야한다면 객체를 비워줍니다.
  if (!obj.shouldSendUsageScroll) scrollObj = {};

  return {
    actionKind: 'UsagePage',
    // pageview는 _화면, usage는 _사용이 붙습니다.
    actionName: obj.actionName.replace('_화면', '_사용'),
    actionType: 'Usage',
    customProps: {
      ...obj.customProps,
      user_action: 'scroll',
    },
    page: obj.page,
    page_meta: obj.page_meta,
    section: obj.section,
    usage: {
      duration: String(differenceInMilliseconds(new Date(), loggingTime)),
      ...scrollObj,
    },
  } as TrackEventObject;
}
